import {
  ButtonsBloc,
  FGNumberInput,
  FGSelectInput,
  FGTextAreaInput,
  FieldGroup,
  InlineButtonContainer,
} from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";
import * as Yup from "yup";

import {
  ERequestState,
  MiscellaneousRequestApi,
  MiscellaneousRequestDto,
  PersonApi,
  RequestApi,
  RequestDto,
  RequestInfoDto,
} from "../../../../api";
import {
  CancelButton,
  FGPeopleSearchInput,
  FGSRSHDateMaskInput,
  InformationTooltip,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../components";
import { useDialog, useEventsContext } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";

export interface IMiscellaneousRequestDetailProps {
  miscellaneousRequestId: number;
  editMode: boolean;
  requestInfo: RequestInfoDto;
}

export const MiscellaneousRequestDetail: React.FunctionComponent<IMiscellaneousRequestDetailProps> = ({
  miscellaneousRequestId,
  editMode,
  requestInfo,
}) => {
  const { masterRequestId = "0" } = useParams<{
    masterRequestId: string;
  }>();
  const { t } = useTl();
  const api = useApiService(MiscellaneousRequestApi);
  const history = useHistory();
  const personApi = useApiService(PersonApi);
  const { openNewTab } = useWindow();
  const { showDialogPromise } = useDialog();

  const [articles, aloading] = useReferential((a) => a.getArticles(false), true, []);

  const { data, loading, saving, saveItem, refresh } = useCrudApi<MiscellaneousRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = miscellaneousRequestId) => {
          if (id > 0) {
            return api.get(id);
          } else if (+masterRequestId > 0) {
            return api.getSubRequests(+masterRequestId);
          } else {
            return new MiscellaneousRequestDto({
              request: new RequestDto({
                requestDate: new Date(),
                status: ERequestState.REQUESTED,
                remark: "",
              }),
            }).toJSON();
          }
        },
        saveApiFn: async (d: MiscellaneousRequestDto) => api.save(d),
        onSavedRoute: (d) => `${ERoutes.miscellaneousRequest}/${d.miscellaneousRequestId}/detail/edit`,
      }),
      [api, masterRequestId, miscellaneousRequestId]
    ),
    [miscellaneousRequestId]
  );

  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      let isBanned = false;
      if (miscellaneousRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [miscellaneousRequestId, personApi, saveItem, showDialogPromise, t]
  );

  const statusEditable = React.useMemo(() => {
    if (data) {
      if (data.request.status) {
        return data.request.status === ERequestState.REQUESTED;
      } else {
        return true;
      }
    }
    return false;
  }, [data]);

  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      articleId: Yup.number().nullable().required(t(ETLCodes.Required)),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("is-not-deseased", t(ETLCodes.PersonISDeceased), async function (value) {
            if (miscellaneousRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await personApi.isDeceased(value);
              return !result.value;
            }
            return true;
          })
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (miscellaneousRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const result = await personApi.missingAddressInformationCheck(value);
                return result.value;
              }
              return true;
            }
            return true;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [data, miscellaneousRequestId, personApi, t]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  const onCancel = React.useCallback(() => history.push(`${ERoutes.miscellaneousRequest}`), [history]);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  return (
    <SmallFormGenerator
      initialValues={data}
      editMode={editMode}
      onSubmit={saveCheck}
      validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
      loading={loading}
      saving={saving || saveChecking}
      onCancel={onCancel}
      hideButtons={data?.request?.status === ERequestState.EXECUTED}
    >
      <FieldGroup
        columns={2}
        fieldsetProps={{
          title: t(ETLCodes.RequestData),
          rightElement: miscellaneousRequestId > 0 && (
            <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
          ),
        }}
      >
        <div>
          <FGSRSHDateMaskInput
            name="request.requestDate"
            label={t(ETLCodes.RequestDate)}
            requiredMark
            readonly={!statusEditable || data?.request?.isBlocked || requestInfo?.masterRequestId > 0}
          />
          {data?.request?.status === ERequestState.EXECUTED && (
            <FieldGroup columns={2}>
              <FGSRSHDateMaskInput name="request.executionDate" label={t(ETLCodes.ClosingDate)} readonly={true} />
            </FieldGroup>
          )}
          <FGSelectInput
            name="articleId"
            label={t(ETLCodes.Article)}
            items={articles}
            loading={aloading}
            onItemViewClick={(item) => openNewTab(`${ERoutes.article}/${item}/detail/view`)}
            readonly={!statusEditable || data?.request?.isBlocked}
          />
          <FGNumberInput
            name="quantity"
            label={t(ETLCodes.Quantity)}
            min={1}
            readonly={requestInfo?.requestStateCode !== ERequestState.REQUESTED}
          />
          <FGPeopleSearchInput
            name="request.personId"
            label={t(ETLCodes.Contact)}
            onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
            readonly={!statusEditable || data?.request?.isBlocked || requestInfo?.masterRequestId > 0}
            requiredMark
            info={<InformationTooltip tooltipMessage={ETLCodes.MustBeBelgianOwner} />}
            showWarning={true}
          />
        </div>

        <FGTextAreaInput
          name="request.remark"
          label={t(ETLCodes.Remark)}
          readonly={data?.request?.status === ERequestState.EXECUTED}
          rows={5}
        />
      </FieldGroup>
      {data?.request?.status === ERequestState.EXECUTED && (
        <InlineButtonContainer>
          <div />
          <ButtonsBloc style={{ float: "right" }}>
            <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.miscellaneousRequest}`)} />
          </ButtonsBloc>
        </InlineButtonContainer>
      )}
    </SmallFormGenerator>
  );
};
